import ChevronFilters from '@assets/icons/chevron-down.svg';
import i18n from '@/utils/i18n';
import React, { useEffect, useMemo, useState } from 'react';
import { BaseActionSheet } from './base-action-sheet';
import {
  createMaxDecimalDigitsValidator,
  TextInput,
  validationFunctions,
  Validations,
} from './text-input';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, TYPOGRAPHY } from '@/styles';
import {
  formatPriceInMicrosWithoutSymbol,
  getCurrencySymbol,
  MICRO_CONVERSION,
} from '@/utils/money';
import { List } from './list';
import { ListOption } from './list-item';
import { PriceTypes } from '@/enums';
import { Text, TouchableOpacity } from 'react-native';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';
import { VariantPackPricesResponse } from '@/types';

interface PriceConfig {
  priceInMicros: number;
}

interface TagPrintingTemplateCustomPriceFieldProps {
  availablePriceTypes: PriceTypes[];
  customPriceInMicros: number | null;
  offlinePrices?: VariantPackPricesResponse;
  setCustomPriceInMicros: React.Dispatch<React.SetStateAction<number | null>>;
  variantPackUUID: string;
}

type PriceConfigListOption = ListOption<PriceConfig>;

const customPriceInputValidations = [
  (input: string) => {
    if (input.endsWith('.')) {
      return true;
    }

    return (
      validationFunctions[Validations.POSITIVE](input) &&
      createMaxDecimalDigitsValidator(0, 2)(input)
    );
  },
];

export const TagPrintingTemplateCustomPriceField: React.FC<
  TagPrintingTemplateCustomPriceFieldProps
> = ({
  availablePriceTypes,
  customPriceInMicros,
  offlinePrices,
  setCustomPriceInMicros,
  variantPackUUID,
}) => {
  const [styles, theme] = useStyles();
  const { currencyCode, getVariantPackInfo } = useVariantPackInfoContext();
  const isOfflineModeActive = useOfflineModeActive();
  const [displayValue, setDisplayValue] = useState(
    typeof customPriceInMicros === 'number'
      ? formatPriceInMicrosWithoutSymbol(customPriceInMicros)
      : '',
  );
  const [showSetPriceSheet, setShowSetPriceSheet] = useState(false);

  useEffect(() => {
    setDisplayValue(
      typeof customPriceInMicros === 'number'
        ? formatPriceInMicrosWithoutSymbol(customPriceInMicros)
        : '',
    );
  }, [customPriceInMicros]);

  const variantPackInfo = getVariantPackInfo(variantPackUUID);

  /**
   * Creates a list of price configuration options derived from the variant pack information.
   * Each option contains metadata with the price in micros, a display title, and a value representing the price type.
   *
   * @returns {PriceConfigListOption[]} An array of price configuration options.
   */
  const listItemData = useMemo<PriceConfigListOption[]>(
    () =>
      [
        {
          priceInMicros:
            isOfflineModeActive && offlinePrices?.suggested_retail_price
              ? offlinePrices.suggested_retail_price
              : variantPackInfo?.suggestedRetailPriceInMicros,
          type: PriceTypes.SRP,
        },
        {
          priceInMicros:
            isOfflineModeActive &&
            offlinePrices?.manufacturer_suggested_retail_price
              ? offlinePrices.manufacturer_suggested_retail_price
              : variantPackInfo?.manufacturerSuggestedRetailPriceInMicros,
          type: PriceTypes.MSRP,
        },
      ].reduce<ListOption<PriceConfig>[]>((acc, { priceInMicros, type }) => {
        if (
          availablePriceTypes.includes(type) && // If the price type is enabled
          typeof priceInMicros === 'number' // If the price is a defined number
        ) {
          acc.push({
            metadata: { priceInMicros },
            title: i18n.t('set_price_to', {
              value: i18n.t(type.toLowerCase()),
            }),
            value: type,
          });
        }
        return acc;
      }, []),
    [
      isOfflineModeActive,
      offlinePrices,
      variantPackInfo?.suggestedRetailPriceInMicros,
      variantPackInfo?.manufacturerSuggestedRetailPriceInMicros,
      availablePriceTypes,
    ],
  );

  /**
   * Handles the click event for setting the price from the list item.
   * It formats the price from micros to a decimal value and sets it as the custom price.
   * Also, it hides the price selection sheet.
   *
   * @param {ListOption<PriceConfig>} item - The selected price configuration option.
   */
  const handleSetPriceListItemClick = (item: ListOption<PriceConfig>) => {
    setCustomPriceInMicros(item.metadata!.priceInMicros);
    setShowSetPriceSheet(false);
  };

  /**
   * Handles the click event for setting the price pill.
   * If there is only one price option, it sets the custom price directly.
   * Otherwise, it shows the price selection sheet.
   */
  const handleSetPricePillClick = () => {
    listItemData.length === 1
      ? setCustomPriceInMicros(listItemData[0].metadata!.priceInMicros)
      : setShowSetPriceSheet(true);
  };

  return (
    <>
      <Text style={styles.fieldKey}>{i18n.t('custom_price')}</Text>
      <TextInput
        accessibilityLabel={i18n.t('custom_price')}
        containerStyle={styles.customPriceInput}
        keyboardType="decimal-pad"
        leftElement={
          <Text style={[TYPOGRAPHY.pSemiBold, styles.currencySymbol]}>
            {getCurrencySymbol(currencyCode)}
          </Text>
        }
        onChangeText={(text) => {
          setDisplayValue(text);
          if (text.endsWith('.')) {
            return;
          }

          const parsedNumber = parseFloat(text);
          if (!isNaN(parsedNumber)) {
            setCustomPriceInMicros(parsedNumber * MICRO_CONVERSION);
          } else {
            setDisplayValue('');
            setCustomPriceInMicros(null);
          }
        }}
        placeholder="--"
        rightElement={
          listItemData.length > 0 ? (
            <TouchableOpacity
              onPress={handleSetPricePillClick}
              style={styles.inputContainer}
            >
              <Text style={styles.inputContainerText}>
                {listItemData.length === 1
                  ? i18n.t('set_to_single', {
                      value: i18n.t(
                        listItemData[0].value.toString().toLowerCase(),
                      ),
                    })
                  : i18n.t('set_to_multiple')}
              </Text>
              {listItemData.length > 1 && (
                <ChevronFilters
                  fill={theme.colors.primary}
                  height={8}
                  width={8}
                />
              )}
            </TouchableOpacity>
          ) : null
        }
        showDoneButtonAccessoryView
        testID="custom-price-input"
        validators={customPriceInputValidations}
        value={displayValue}
      />
      {listItemData.length > 0 ? (
        <BaseActionSheet
          isVisible={showSetPriceSheet}
          onClose={() => setShowSetPriceSheet(false)}
        >
          <List data={listItemData} onSelect={handleSetPriceListItemClick} />
        </BaseActionSheet>
      ) : null}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  currencySymbol: {
    color: theme.colors.secondary,
    fontSize: 16,
    lineHeight: 22,
  },
  customPriceInput: {
    borderColor: theme.colors.divider,
    borderWidth: 1,
    flex: 1,
  },
  fieldKey: {
    ...FONTS.primarySemiBold,
    color: theme.sk.color.text.secondary,
    fontSize: 14,
    marginBottom: 8,
  },
  inputContainer: {
    alignItems: 'center',
    backgroundColor: theme.colors.offWhite,
    borderRadius: 24,
    flex: 1,
    flexDirection: 'row',
    gap: 8,
    maxHeight: 24,
    paddingHorizontal: 12,
  },
  inputContainerText: {
    ...FONTS.primarySemiBold,
    color: theme.colors.primary,
    fontSize: 14,
    lineHeight: 14,
  },
}));
